/**
 * StationStatus HTTP client — `GET /stations/status` with ETag/304 support.
 *
 * Extracted from Suggester's `clients.ts#fetchStatuses` into a standalone
 * domain-agnostic client (spec §3.1 rename: `_SuggesterStatusClient` →
 * `_StationStatusClient`). Suggester re-uses this client via the shared
 * module instead of owning it directly.
 *
 * Cross-platform contract (spec §8): If-None-Match / 304 semantics are
 * identical on iOS `LiveStationStatusClient` and Android `OkHttpStationStatusClient`.
 */
import type { StationStatus, StationStatusSnapshot } from './types'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** `fetch`-compatible function — injectable for tests / MSW. */
export type FetchFn = (input: string, init?: RequestInit) => Promise<Response>

/**
 * Discriminated result of a status fetch (mirrors iOS / Android sealed class):
 * - `changed` — server returned 200 with a new snapshot; `data` is the new snapshot.
 * - `notModified` — server returned 304; cache is still fresh.
 * - `error` — network failure or non-2xx/304 response; `error` carries a message.
 */
export type StationStatusClientResult =
  | { kind: 'changed'; data: StationStatusSnapshot; etag: string }
  | { kind: 'notModified' }
  | { kind: 'error'; error: Error }

/**
 * Domain-agnostic status client interface.
 * Mirrors iOS `_StationStatusClient` protocol.
 */
export interface StationStatusClient {
  /**
   * Fetch the status snapshot. Pass `ifNoneMatch` from the last cached ETag
   * to receive 304 when the data hasn't changed.
   */
  fetchStatuses(ifNoneMatch?: string): Promise<StationStatusClientResult>
}

// ---------------------------------------------------------------------------
// Wire DTO
// ---------------------------------------------------------------------------

/**
 * Wire shape of a single station status entry from the server.
 * Mirrors the server JSON contract (§8 spec, unchanged).
 */
interface StationStatusDTO {
  readonly severity: string
  readonly closed: boolean
  readonly title?: string
  readonly message?: string
  readonly appearance?: string
  readonly fromDate?: string
  readonly toDate?: string
  readonly lineIds?: readonly string[]
}

// ---------------------------------------------------------------------------
// Constants (cross-platform STRICT)
// ---------------------------------------------------------------------------

/**
 * Max response body size — cross-platform contract with iOS
 * `LiveStationStatusClient` (1 MB). Rejects responses larger than this.
 */
const MAX_BODY_BYTES = 1_000_000

/**
 * Per-request timeout — cross-platform with the iOS client's `timeoutInterval`.
 * Aborts a hung request so the preloader can keep the 60 s cadence intact.
 */
const REQUEST_TIMEOUT_MS = 10_000

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

function timeoutSignal(): AbortSignal {
  return AbortSignal.timeout(REQUEST_TIMEOUT_MS)
}

async function parseJsonCapped(response: Response): Promise<unknown> {
  const declared = response.headers.get('content-length')
  if (declared !== null && Number(declared) > MAX_BODY_BYTES) {
    throw new Error(`StationStatus response too large: ${declared} > ${MAX_BODY_BYTES}`)
  }
  const text = await response.text()
  if (new TextEncoder().encode(text).length > MAX_BODY_BYTES) {
    throw new Error(`StationStatus response too large: > ${MAX_BODY_BYTES} bytes`)
  }
  return text.length === 0 ? {} : (JSON.parse(text) as unknown)
}

function dtoToStatus(dto: StationStatusDTO): StationStatus {
  const severity = dto.severity === 'closure' ? 'closure' : 'advisory'
  return {
    severity,
    closed: dto.closed,
    payload: {
      title: dto.title,
      message: dto.message,
      appearance: dto.appearance,
      fromDate: dto.fromDate,
      toDate: dto.toDate,
      lineIds: dto.lineIds ?? [],
    },
  }
}

/**
 * Build the status client for a given base URL.
 *
 * @param baseUrl - Backend base URL, e.g. `/api/v1` or `http://localhost:8001/api/v1`.
 * @param fetchFn - Optional `fetch` override (tests / SSR).
 */
export function createStationStatusClient(baseUrl: string, fetchFn?: FetchFn): StationStatusClient {
  const doFetch: FetchFn = fetchFn ?? ((input, init) => fetch(input, init))

  const origin =
    typeof window !== 'undefined' && window.location !== undefined
      ? window.location.origin
      : /* v8 ignore next */ undefined

  const resolveUrl = (path: string): string => {
    const spec = `${baseUrl.replace(/\/$/, '')}${path}`
    return origin !== undefined ? new URL(spec, origin).toString() : new URL(spec).toString()
  }

  return {
    async fetchStatuses(ifNoneMatch?: string): Promise<StationStatusClientResult> {
      try {
        const headers: Record<string, string> = {}
        if (ifNoneMatch !== undefined) {
          headers['If-None-Match'] = ifNoneMatch
        }
        const response = await doFetch(resolveUrl('/stations/status'), {
          signal: timeoutSignal(),
          headers,
        })

        if (response.status === 304) {
          return { kind: 'notModified' }
        }

        if (!response.ok) {
          return {
            kind: 'error',
            error: new Error(`StationStatus /stations/status failed: ${response.status}`),
          }
        }

        const etag = response.headers.get('etag') ?? ''
        // Wire envelope is `{ version, stale, stations }` — see the server
        // `statusSnapshotResponse` DTO (metro-server backend/search/dto.go).
        // `version` / `stale` are tolerated (decoded, unused): `stale` is a
        // freshness signal the web client does not yet act on; adding a
        // behaviour for it is a separate change, not a decode requirement.
        const body = (await parseJsonCapped(response)) as {
          version?: string
          stale?: boolean
          stations?: Readonly<Record<string, StationStatusDTO>>
        }
        const dtosMap = body.stations ?? {}
        const snapshot: Record<string, StationStatus> = {}
        for (const [naptanId, dto] of Object.entries(dtosMap)) {
          snapshot[naptanId] = dtoToStatus(dto)
        }

        return { kind: 'changed', data: snapshot, etag }
      } catch (err) {
        return {
          kind: 'error',
          error: err instanceof Error ? err : new Error(String(err)),
        }
      }
    },
  }
}
